/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { z } from 'zod';
import { PlantConfiguration } from '../data';
import { leveredFinancingInputsSchema } from '../fimo-financing/schemas';
import { fromObjectEntries, objectKeys } from '../utils';
import { formNewConfigPayload, } from './config';
import { componentIdSchema } from './core';
import { essCostOutputsSchema } from './ess-costing';
import { CostItemBasis, CostItemDirection, CostItemScope, ValidDepreciationValues, } from './fimo-enums';
export * from './fimo-enums';
// IRS convention for the first tax year of 5-year MACRS. Half-year treats every asset as placed in
// service mid-year (20% in year one); mid-quarter uses the midpoint of each asset's
// placed-in-service quarter.
export var FiveYearMacrsConvention;
(function (FiveYearMacrsConvention) {
    FiveYearMacrsConvention["HALF_YEAR"] = "half_year";
    FiveYearMacrsConvention["MID_QUARTER"] = "mid_quarter";
})(FiveYearMacrsConvention || (FiveYearMacrsConvention = {}));
export const fiveYearMacrsConventionLabels = {
    [FiveYearMacrsConvention.HALF_YEAR]: 'Half-Year',
    [FiveYearMacrsConvention.MID_QUARTER]: 'Mid-Quarter',
};
// Label for the convention a FIMO run actually used, read from the run's stored inputs. FIMO
// 3.14+ always records it (Half-Year when the config omits it); earlier engines never did and
// used the legacy first-year proration whatever the config selected.
export const runMacrsConventionLabel = (convention) => convention ? fiveYearMacrsConventionLabels[convention] : 'Legacy (before FIMO 3.14)';
export var NetworkUpgradePaymentTiming;
(function (NetworkUpgradePaymentTiming) {
    NetworkUpgradePaymentTiming["AT_COD"] = "at_cod";
    NetworkUpgradePaymentTiming["FIXED_DATE"] = "fixed_date";
    NetworkUpgradePaymentTiming["CONSTRUCTION_CURVE"] = "construction_curve";
})(NetworkUpgradePaymentTiming || (NetworkUpgradePaymentTiming = {}));
export const devFeePaidDateOptions = ['cod', 'ntp'];
const costSchema = z.strictObject({
    value: z.number(),
});
const assetScheduleSchema = z.strictObject({
    contracted_term: z.number(),
    merchant_tail_term: z.number(),
});
const baseSchema = z.strictObject({
    name: z.string().nullable().optional(),
    cod: z.iso.datetime(),
    ntp: z.iso.datetime().optional(),
    fimo_start_date: z.iso.datetime().optional(),
    plant_configuration: z.array(z.enum(PlantConfiguration)),
    pv: assetScheduleSchema.nullable().optional(),
    ess: assetScheduleSchema.nullable().optional(),
    land_purchase_date: z.iso.datetime().optional(),
    land_lease_start_date: z.iso.datetime().optional(),
});
const annualCostsSchema = z.strictObject({
    administration_services: z.number().min(0).nullable().optional(),
    bn_administration_services: z.number().min(0).nullable().optional(),
    professional_services: z.number().min(0).nullable().optional(),
    nerc_compliance_costs: z.number().min(0).nullable().optional(),
    labor_cost_adder_prevailing_wage: z.number().min(0).nullable().optional(),
    other_operating_costs: z.number().min(0).nullable().optional(),
    emsa_costs: z.number().min(0).nullable().optional(),
});
const wheelingChargeSchema = z.strictObject({
    first_year: z.number().min(0),
    escalation: z.number().min(0).max(1),
});
const wheelingChargesSchema = z.strictObject({
    contract_period: wheelingChargeSchema.nullable().optional(),
    merchant_period: wheelingChargeSchema.nullable().optional(),
});
const pvCostOutputsSchema = z.strictObject({
    cost: z.number(),
    cost_per_kw_dc: z.number(),
    labor_cost: z.number(),
});
const opexEscalatorsSchema = z.strictObject({
    ...fromObjectEntries(objectKeys(annualCostsSchema.shape).map((field) => [
        field,
        z.number().min(0).max(0.5).nullable().optional(),
    ])),
    mowing: z.number().min(0).max(0.5).nullable().optional(),
    o_and_m_pv: z.number().min(0).max(0.5).nullable().optional(),
    panel_washing: z.number().min(0).max(0.5).nullable().optional(),
    insurance_pv: z.number().min(0).max(0.5).nullable().optional(),
    replacement_parts: z.number().min(0).max(0.5).nullable().optional(),
    staffing_and_facilities: z.number().min(0).max(0.5).nullable().optional(),
    inverter_opex: z.number().min(0).max(0.5).nullable().optional(),
    generic_opex_adder_escalator: z.number().min(0).nullable().optional(),
    restoration_bond: z.number().min(0).max(0.5).nullable().optional(),
    ess_insurance: z.number().min(0).max(0.5).nullable().optional(),
});
const inverterOpexSchema = z.strictObject({
    num_inverters: z.number().min(0).int(),
    yr1_cost_per_inverter: z.number().min(0),
});
/** Units allowed for each asset scope (mirrors the backend's basis/scope rule). */
export const COST_ITEM_SCOPE_BASES = {
    [CostItemScope.PROJECT]: [CostItemBasis.USD],
    [CostItemScope.PV]: [
        CostItemBasis.USD,
        CostItemBasis.USD_PER_KW_DC,
        CostItemBasis.USD_PER_KW_AC,
        CostItemBasis.USD_PER_MWH_NET_GEN,
    ],
    [CostItemScope.ESS]: [CostItemBasis.USD, CostItemBasis.USD_PER_KWH_ESS],
};
/** Post-COD capital is a capital addition, so it needs one of FIMO's four depreciable schedules. */
export const DEPRECIABLE_COST_ITEM_SCHEDULES = [
    ValidDepreciationValues.FIVE_YEAR_MACRS,
    ValidDepreciationValues.TWELVE_YEAR_SL,
    ValidDepreciationValues.TWENTY_YEAR_SL,
    ValidDepreciationValues.THIRTY_FIVE_YEAR_SL,
];
export const costItemOrigins = ['manual', 'csv', 'xlsx', 'clipboard', 'migration'];
const nonNegativeValuesSchema = z.array(z.number().min(0)).min(1);
export const costItemScheduleSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('flat_escalating'),
        first_year_value: z.number().min(0),
        escalator: z.number().min(0).max(0.5),
        start_op_year: z.int().min(1),
        end_op_year: z.int().min(1).nullable(),
    }),
    z.strictObject({
        kind: z.literal('annual_series'),
        time_basis: z.enum(['operating_year', 'calendar_year']),
        start_year: z.int(),
        values: nonNegativeValuesSchema,
    }),
    z.strictObject({
        kind: z.literal('monthly_series'),
        start_month: z.iso.datetime(),
        values: nonNegativeValuesSchema,
    }),
    z.strictObject({
        kind: z.literal('dated_payments'),
        payments: z.array(z.strictObject({ date: z.iso.datetime(), value: z.number().min(0) })).min(1),
    }),
]);
/**
 * A named custom cost (OPEX or capital) that owns its schedule. Items are always additive; the
 * backend (FIMO `CostItem`) is the authority for validation and calculation.
 */
export const costItemSchema = z
    .strictObject({
    // Identifies the item within this configuration so the editor can update, delete or merge it
    // and match its result row, even after a rename (names are free text). Not referenced across
    // runs or versions.
    id: z.uuid(),
    name: z.string().trim().min(1, 'Name is required'),
    scope: z.enum(CostItemScope),
    treatment: z.discriminatedUnion('kind', [
        z.strictObject({ kind: z.literal('opex') }),
        z.strictObject({
            kind: z.literal('capital'),
            depreciation_schedule: z.enum(ValidDepreciationValues),
        }),
    ]),
    direction: z.enum(CostItemDirection),
    basis: z.enum(CostItemBasis),
    schedule: costItemScheduleSchema,
    /** Free-text reporting group (e.g. "Community & donations"); results roll items up by it. */
    group: z.string().trim().max(80, 'Group must be at most 80 characters').nullable().optional(),
    notes: z.string().nullable().optional(),
    source: z
        .strictObject({
        origin: z.enum(costItemOrigins),
        reference: z.string().nullable().optional(),
    })
        .nullable()
        .optional(),
})
    .check((ctx) => {
    const { basis, scope, schedule } = ctx.value;
    if (!COST_ITEM_SCOPE_BASES[scope].includes(basis)) {
        ctx.issues.push({
            code: 'custom',
            message: COST_ITEM_SCOPE_BASES[CostItemScope.PV].includes(basis)
                ? 'Capacity and generation rates must be PV-scoped'
                : '$/kWh-ESS rates must be ESS-scoped',
            path: ['scope'],
            input: scope,
        });
    }
    if (schedule.kind === 'dated_payments' && basis !== CostItemBasis.USD) {
        ctx.issues.push({
            code: 'custom',
            message: 'Dated payments must be in USD',
            path: ['basis'],
            input: basis,
        });
    }
    if (schedule.kind === 'flat_escalating' &&
        schedule.end_op_year !== null &&
        schedule.end_op_year < schedule.start_op_year) {
        ctx.issues.push({
            code: 'custom',
            message: 'End year cannot be before start year',
            path: ['schedule', 'end_op_year'],
            input: schedule.end_op_year,
        });
    }
});
/**
 * The retired one-time cost lists. Configs saved before cost items always carry them, often
 * empty, so an empty list is still accepted when a stored config is posted back; a non-empty one
 * must go through the cost item migration first.
 */
const retiredOneTimeCostsSchema = z
    .array(z.unknown())
    .max(0, 'One-time costs were replaced by cost items; run the cost item migration first')
    .optional();
const expensesSchema = z.strictObject({
    one_time_post_cod_costs: retiredOneTimeCostsSchema,
    wheeling_charges: wheelingChargesSchema.nullable().optional(),
    annual_costs: annualCostsSchema.nullable().optional(),
    pv_inverter_opex: inverterOpexSchema.optional(),
    pv_opex_first_year: z.number().min(0).optional(),
    insurance_pv: z.number().min(0).optional(),
    pv_opex_breakdown: z.record(z.string(), pvCostOutputsSchema).nullable().optional(),
    escalators: opexEscalatorsSchema.nullable().optional(),
    ess_opex_first_year: z.number().min(0).optional(),
    ess_insurance_per_kwh_ac: z.number().min(0).optional().nullable(),
    lc_amount: z.number().min(0),
    lc_annual_rate: z.number().min(0).max(1).nullable(),
    lc_day_count_basis: z.int().min(1).nullable().optional(),
    opex_annual_growth_rate: z.number().min(0).max(1).nullable(),
    electricity_producer_tax: z.number().min(0).max(20).nullable().optional(),
    restoration_bond: z.number().min(0).nullable().optional(),
    gross_receipts_tax: z.number().min(0).max(100).nullable().optional(),
    generic_opex_adder_first_year: z.number().min(0).nullable().optional(),
    transmission_easement_payment: z.number().optional().nullable(),
    ess_annual_om: z.array(z.number()).optional().nullable(),
    ess_aux_load: z.array(z.number()).optional().nullable(),
});
const essCostsSchema = z.strictObject({
    augmentations: z.array(z.strictObject({
        breakdown: z.record(z.string(), essCostOutputsSchema),
        date: z.iso.datetime(),
    })),
    power_mw_ac: z.number().min(0),
    capacity_mwh_ac: z.number().min(0),
    residual_value: z.number().min(0).optional(),
});
const pvCostSchema = z.strictObject({
    breakdown: z.record(z.string(), pvCostOutputsSchema).nullable().optional(),
    ac_capacity_kw: z.number(),
    dc_capacity_kw: z.number(),
});
const plantSchema = z.strictObject({
    pv: pvCostSchema.optional(),
    ess: essCostsSchema.optional(),
});
const transmissionSchema = z.strictObject({
    reimb_network_upgrade_cost: costSchema,
    gen_tie_mitigation: costSchema,
    non_reimb_network_upgrade_cost: costSchema,
    network_upgrade_reimbursement_period: z.number().min(0).optional().nullable(),
    network_upgrade_reimbursement_interest_rate: z.number().min(0).max(1).optional().nullable(),
    lc_lgia_size_construction_period: z.number().min(0).nullable().optional(),
    lc_lgia_cost_construction_period: z.number().min(0).max(1).nullable().optional(),
    lc_lgia_size_development_period: z.number().min(0).nullable().optional(),
    lc_lgia_cost_development_period: z.number().min(0).max(1).nullable().optional(),
    lc_lgia_posting_date: z.string().nullable().optional(),
    network_upgrade_payment_timing: z.enum(NetworkUpgradePaymentTiming).optional(),
    network_upgrade_payment_date: z.string().optional().nullable(),
});
const landLeaseEscalationStepsSchema = z.strictObject({
    step_rate: z.number().min(0).max(1),
    step_frequency_years: z.int().min(1),
    first_step_operating_year: z.int().min(1),
});
const landSchema = z.strictObject({
    taxable_value: z.number(),
    lease_mitigation_cost_per_acre: z.number().optional(),
    purchase_mitigation_cost_per_acre: z.number().optional(),
    annual_lease_escalation_rate: z.number().nullable().optional(),
    lease_escalation_steps: landLeaseEscalationStepsSchema.nullable().optional(),
    include_sale_proceeds: z.boolean().default(true),
    residual_value_escalation_rate: z.number().min(-1).max(0.1).optional(),
    purchase_acreage: z.number().min(0).optional(), // deprecated
    lease_acreage: z.number().min(0).optional(), // deprecated
    purchase_cost_per_acre: z.number().min(0).optional(), // deprecated
    annual_lease_rate: z.number().min(0).optional(), // deprecated
});
const testEnergySchema = z.strictObject({
    volume_factor: z.number().min(0).max(1).nullable().optional(),
    price_factor: z.number().min(0).max(1).nullable().optional(),
    duration_months: z.number().min(0).int().nullable().optional(),
});
const constructionSchema = z.strictObject({
    insurance_cost: costSchema,
    management_cost: costSchema,
    project_dev_cost: costSchema,
    test_energy: testEnergySchema.nullable().optional(),
    lc_ppa_size_construction_period: z.number().min(0).nullable().optional(),
    lc_ppa_cost_construction_period: z.number().min(0).max(1).nullable().optional(),
    lc_ppa_size_development_period: z.number().min(0).nullable().optional(),
    lc_ppa_cost_development_period: z.number().min(0).max(1).nullable().optional(),
    lc_ppa_posting_date: z.string().nullable().optional(),
});
export const depreciationScheduleSchema = z.strictObject({
    gentie_line_mitigation_costs: z.enum(ValidDepreciationValues),
    reimb_network_upgrades_costs: z.enum(ValidDepreciationValues),
    nonreimb_network_upgrades_costs: z.enum(ValidDepreciationValues),
    land_lease_costs: z.enum(ValidDepreciationValues),
    land_purchase_costs: z.enum(ValidDepreciationValues),
    land_mitigation_costs: z
        .enum(ValidDepreciationValues)
        .nullish()
        .transform((value) => value ?? ValidDepreciationValues.INDIRECT),
    construction_insurance_costs: z.enum(ValidDepreciationValues),
    working_capital: z.enum(ValidDepreciationValues),
    dev_fee: z.enum(ValidDepreciationValues),
    test_energy_revenue: z.enum(ValidDepreciationValues),
    capex_property_taxes: z.enum(ValidDepreciationValues),
    generic_capex_adjustment: z.enum(ValidDepreciationValues),
    lc_lgia_cost_construction_period: z.enum(ValidDepreciationValues),
    lc_ppa_cost_construction_period: z.enum(ValidDepreciationValues),
    lc_upfront_fees_post_fc_ppa_and_lgia: z.enum(ValidDepreciationValues),
    total_pv_capex: z.enum(ValidDepreciationValues),
    total_ess_capex: z.enum(ValidDepreciationValues),
});
const capexSchema = z.strictObject({
    one_time_pre_cod_costs: retiredOneTimeCostsSchema,
    plant: plantSchema,
    transmission: transmissionSchema,
    land: landSchema,
    construction: constructionSchema,
    fmv_step_up: z.number(),
    // FIMO rejects zero working capital, so a project without any uses a nominal $0.01.
    working_capital: z
        .number()
        .positive({
        error: 'Working capital must be greater than $0; enter $0.01 if the project has none',
    })
        .nullable()
        .optional(),
    lc_upfront_fees_post_fc_ppa_and_lgia: z.number().min(0).nullable().optional(),
    pv_generic_capex_adder: z.number().nullable().optional(),
    ess_generic_capex_adder: z.number().nullable().optional(),
    itc_insurance_rate: z.number().min(0).max(1).optional(),
    ptc_insurance_rate: z.number().min(0).max(1).optional(),
    depreciation_schedule: depreciationScheduleSchema.optional(),
});
const propertyTaxSchema = z.strictObject({
    rate: z.number().min(0).max(1).nullable(),
    abatement_rate: z.number().min(0).max(1),
    assessment_rate: z.number().min(0).max(1),
    tax_rate_escalation: z.number().min(0).max(1).optional().nullable(),
});
const propertyTaxesSchema = z.strictObject({
    real: z.strictObject({
        purchase_tax_base_escalation: z.number().min(0).max(1).optional(),
        lease_tax_base_escalation: z.number().min(0).max(1).optional(),
        ...propertyTaxSchema.shape,
    }),
    personal: propertyTaxSchema,
    construction_pv_good: z.number().min(0).max(1).optional().nullable(),
    construction_ess_good: z.number().min(0).max(1).optional().nullable(),
    construction_tax_adjustment: z.number().optional().nullable(),
    annual_pv_good_schedule: z.array(z.number()).nullable().optional(),
    annual_ess_good_schedule: z.array(z.number()).nullable().optional(),
    annual_tax_adjustments: z.array(z.number()).optional(),
    assets_active_through_end_of_life_year: z.boolean().optional(),
});
const incomeTaxesSchema = z.strictObject({
    corporate_rate: z.number().min(0).max(1).nullable(),
    state_rate: z.number().min(0).max(1).nullable(),
    pv_sales_use_tax_rate: z.number().min(0).max(1),
    ess_sales_use_tax_rate: z.number().min(0).max(1),
    itc_rate: z.number().min(0).max(1).optional(),
    percent_itc_eligible: z.number().min(0).max(1).optional(),
    percent_itc_adder: z.number().min(0).max(0.2).optional(),
    percent_itc_market_transfer: z.number().min(0).max(1).optional(),
    ptc_escalation_rate: z.number().min(0).max(1).optional(),
    ptc_base_rate: z.number().min(0).max(50).optional(),
    ptc_base_rate_multiplier: z.number().optional(),
    ptc_rate_percent_market_transfer: z.number().min(0).max(1).optional(),
    ptc_tenor: z.number().min(5).max(50).int().optional(),
    ptc_base_year: z.number().min(2022).max(2030).int().optional(),
    // Calendar year (JSON object key) -> statutory PTC rate in $/MWh
    ptc_statutory_rate_by_year: z
        .record(z.string().regex(/^\d{4}$/), z.number().min(0))
        .nullable()
        .optional(),
    five_year_macrs_convention: z.enum(FiveYearMacrsConvention).optional(),
});
const taxesSchema = z.strictObject({
    property: propertyTaxesSchema,
    income: incomeTaxesSchema,
});
const structuringSchema = z.strictObject({
    target_atuirr_full_life: z.number().min(0).max(0.2).nullable().optional(),
    target_levered_atirr_full_life: z.number().gt(0).max(1).nullable().optional(),
    // Checked after the levered price solve; Finance's hard minimum is 8%.
    target_levered_atirr_contract: z.number().min(0.08).max(1).nullable().optional(),
    dev_fee_dollar_per_w: z.number().min(0).max(1),
    target_atuirr_contract: z.number().min(0).max(0.2).nullable().optional(),
    target_capacity_payment: z.number().min(0).max(20e3).nullable().optional(),
    target_ppa_price: z.number().min(0).nullable().optional(),
    dev_fee_paid_date: z.enum(devFeePaidDateOptions).optional(),
});
const baseGenAndRevenueSchema = z.strictObject({
    merchant_price_escalation_rate: z.number().min(0).max(1).optional(),
    annual_ppa_escalator: z.number().min(0).max(1).optional(),
    bess_contracted_capacity: z.number().min(0).optional(),
    bess_capacity_price_escalation: z.number().min(0).max(1).optional(),
    price_basis_adder_hub_settled_ppa: z.number().min(0).nullable().optional(),
    price_forecasted_basis_adder_hub_settled_ppa: z.number().min(0).nullable().optional(),
    availability_factor: z.number().min(0).max(1).nullable().optional(),
    curtailment_factor: z.number().min(0).max(1).nullable().optional(),
});
const genAndRevenueSchema = z.strictObject({
    ...baseGenAndRevenueSchema.shape,
    merchant_curves: componentIdSchema.nullable(),
    rec_curves: componentIdSchema.nullable(),
    retail_curves: componentIdSchema.nullable(),
});
const simulationParametersSchema = z.strictObject({
    incl_sensitivity_tables: z.boolean(),
});
export const LEASE_ESCALATION_ONE_OF_ERROR = 'Provide exactly one of an annual lease escalation rate or stepped lease escalation';
export const PTC_RATE_TABLE_SENSITIVITY_ERROR = "PTC statutory rate table can't be used with sensitivity tables; turn off sensitivities or remove the table";
// A lease escalates either annually or in steps, never both or neither
const pushLeaseEscalationIssue = (ctx) => {
    const { annual_lease_escalation_rate: annualRate, lease_escalation_steps: steps } = ctx.value.capex.land;
    if ((annualRate === null || annualRate === undefined) === !steps) {
        ctx.issues.push({
            code: 'custom',
            message: LEASE_ESCALATION_ONE_OF_ERROR,
            path: ['capex', 'land', 'lease_escalation_steps'],
            input: steps,
        });
    }
};
// PTC sensitivities flex ptc_base_rate, which statutory table years ignore
const pushPtcRateTableIssue = (ctx) => {
    const { taxes, simulation_parameters } = ctx.value;
    const rateTable = taxes.income.ptc_statutory_rate_by_year;
    if ((simulation_parameters?.incl_sensitivity_tables ?? true) &&
        Object.keys(rateTable ?? {}).length > 0) {
        ctx.issues.push({
            code: 'custom',
            message: PTC_RATE_TABLE_SENSITIVITY_ERROR,
            path: ['taxes', 'income', 'ptc_statutory_rate_by_year'],
            input: rateTable,
        });
    }
};
// The FIMO backend needs an explicit credit election for each asset the project includes, and
// none for an asset it leaves out; checking it here keeps API clients from saving a levered
// config that only fails when it runs.
const pushTaxCreditElectionIssues = (ctx) => {
    const { base, financing } = ctx.value;
    if (!financing)
        return;
    const assets = [
        { key: 'pv', label: 'PV', included: Boolean(base.pv) },
        { key: 'bess', label: 'BESS', included: Boolean(base.ess) },
    ];
    for (const { key, label, included } of assets) {
        const election = financing.tax_credit_elections[key];
        if (included === (election !== null && election !== undefined))
            continue;
        ctx.issues.push({
            code: 'custom',
            message: included
                ? `${label} credit election is required when the project includes ${label}`
                : `${label} credit election is not allowed without ${label}`,
            path: ['financing', 'tax_credit_elections', key],
            input: election,
        });
    }
};
export const fimoSimulationRequestSchema = z
    .strictObject({
    base: baseSchema,
    gen_and_revenue: genAndRevenueSchema,
    expenses: expensesSchema,
    capex: capexSchema,
    taxes: taxesSchema,
    structuring: structuringSchema,
    financing: leveredFinancingInputsSchema.nullable().optional(),
    simulation_parameters: simulationParametersSchema.nullable().optional(),
    // The editor finds items by id, so two items sharing one would be saved and deleted together.
    cost_items: z
        .array(costItemSchema)
        .check((ctx) => {
        const seen = new Set();
        ctx.value.forEach((item, i) => {
            if (seen.has(item.id)) {
                ctx.issues.push({
                    code: 'custom',
                    message: `Cost item id ${item.id} is used by more than one item`,
                    path: [i, 'id'],
                    input: item.id,
                });
            }
            seen.add(item.id);
        });
    })
        .optional(),
})
    .check((ctx) => {
    const { base, capex } = ctx.value;
    pushLeaseEscalationIssue(ctx);
    pushPtcRateTableIssue(ctx);
    pushTaxCreditElectionIssues(ctx);
    const { network_upgrade_payment_date: paymentDate, network_upgrade_payment_timing: timing } = capex.transmission;
    const path = ['capex', 'transmission', 'network_upgrade_payment_date'];
    if (timing === NetworkUpgradePaymentTiming.FIXED_DATE && !paymentDate) {
        ctx.issues.push({
            code: 'custom',
            message: 'Network upgrade payment date is required for fixed-date timing',
            path,
            input: paymentDate,
        });
        return;
    }
    if (timing === NetworkUpgradePaymentTiming.CONSTRUCTION_CURVE && paymentDate !== null) {
        ctx.issues.push({
            code: 'custom',
            message: 'Network upgrade payment date must be null for construction-curve timing',
            path,
            input: paymentDate,
        });
        return;
    }
    if (timing === NetworkUpgradePaymentTiming.AT_COD &&
        (!paymentDate || new Date(paymentDate).valueOf() !== new Date(base.cod).valueOf())) {
        ctx.issues.push({
            code: 'custom',
            message: 'Network upgrade payment date must equal COD for at-COD timing',
            path,
            input: paymentDate,
        });
        return;
    }
    if (paymentDate &&
        ((base.ntp !== undefined && new Date(paymentDate).valueOf() < new Date(base.ntp).valueOf()) ||
            new Date(paymentDate).valueOf() > new Date(base.cod).valueOf())) {
        ctx.issues.push({
            code: 'custom',
            message: 'Network upgrade payment date must be between NTP and COD',
            path,
            input: paymentDate,
        });
    }
});
export const newFimoConfigPayload = formNewConfigPayload(fimoSimulationRequestSchema);
//# sourceMappingURL=fimo.js.map